import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { Seo } from "@/components/Seo";
import ShareDialog from "@/components/ShareDialog";
import { SITE_URL } from "@/lib/seo";
import { Trash2, Wand2, Sparkles, ImageIcon, Plus, Rocket, X, Check, Loader2, Lock, Share2 } from "lucide-react";

const TARGET_LABELS = { website: "Website Store", printful: "Printful", amazon_merch: "Amazon Merch", etsy: "Etsy", shopify: "Shopify" };

export default function Library() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);
  const [pub, setPub] = useState(null);
  const [selected, setSelected] = useState(["website"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [share, setShare] = useState({ open: false, url: null });

  const shareDesign = async (it) => {
    try {
      await api.post(`/studio/designs/${it.id}/share`);
      setShare({ open: true, url: `${SITE_URL}/api/seo/s/${it.id}` });
    } catch { toast.error("Could not create share link"); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/studio/designs").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/publish/targets").then((r) => setTargets(r.data.targets)).catch(() => {});
  }, [load]);

  const del = async (id) => {
    try { await api.delete(`/studio/designs/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const openPublish = (it) => { setPub(it); setSelected(["website"]); setResult(null); };
  const toggleTarget = (t) => setSelected((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const doPublish = async () => {
    if (!pub) return;
    const tgts = selected.length ? selected : ["website"];
    setBusy(true);
    try {
      const r = await api.post("/publish/design", { design_id: pub.id, targets: tgts, title: pub.name });
      setResult(r.data);
      toast.success(`Published "${pub.name}" through the pipeline`);
    } catch (e) { toast.error("Publish failed"); }
    finally { setBusy(false); }
  };

  const shown = items.filter((i) => filter === "all" || (filter === "ai" ? i.kind === "ai" : i.kind === "design"));

  return (
    <div className="min-h-screen bg-app relative pt-12 pb-28" data-testid="library-page">
      <Seo title="Design Library" description="Your personal vault of AI renders and custom apparel designs." path="/library" noindex />
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="font-mono text-[11px] tracking-[0.4em] text-cyan/70">PERSONAL VAULT</div>
            <h1 className="font-head text-4xl font-bold uppercase mt-2">Design <span className="text-cyan text-glow-cyan">Library</span></h1>
          </div>
          <Link to="/studio" data-testid="library-studio-link" className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-4 py-2 text-xs box-glow-cyan hover:bg-cyan hover:text-app">
            <Plus className="w-4 h-4" /> New Design
          </Link>
        </div>

        <div className="flex gap-2 mb-6" data-testid="library-filters">
          {[["all", "All"], ["design", "My Designs"], ["ai", "AI Renders"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`font-mono text-[10px] tracking-widest px-4 py-2 border transition-all ${filter === k ? "border-cyan text-cyan" : "border-white/10 text-slate-400"}`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-cyan/60 font-mono text-xs tracking-widest animate-pulse-glow">LOADING VAULT...</div>
        ) : shown.length === 0 ? (
          <div className="glass clip-chamfer brackets p-14 text-center">
            <ImageIcon className="w-10 h-10 text-cyan/40 mx-auto mb-4" />
            <p className="text-slate-400">Nothing here yet. Upload artwork in the Studio or render images in the Simulation.</p>
            <Link to="/studio" className="btn-hud inline-flex mt-6 border border-cyan/40 text-cyan px-6 py-2.5 text-sm hover:bg-cyan hover:text-app">Open Design Studio</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="library-grid">
            {shown.map((it) => (
              <div key={it.id} className="group relative glass border border-white/10 hover:border-cyan/40 transition-all overflow-hidden" data-testid={`library-item-${it.id}`}>
                <div className="aspect-square bg-app overflow-hidden">
                  <img src={resolveImg(it.url)} alt={it.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-widest mb-1" style={{ color: it.kind === "ai" ? "#A78BFA" : "#00E5FF" }}>
                    {it.kind === "ai" ? <Sparkles className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                    {it.kind === "ai" ? "AI RENDER" : (it.source === "composite" ? "MOCKUP" : "ARTWORK")}
                  </div>
                  <div className="font-body text-sm text-slate-200 truncate">{it.name}</div>
                  <div className="flex items-center gap-2 mt-3">
                    <Link to="/studio" className="flex-1 btn-hud flex items-center justify-center gap-1.5 border border-cyan/40 text-cyan py-2 text-[10px] tracking-widest hover:bg-cyan hover:text-app">
                      <Wand2 className="w-3 h-3" /> USE
                    </Link>
                    <button onClick={() => openPublish(it)} data-testid={`library-publish-${it.id}`}
                      className="p-2 border border-amber/30 text-amber hover:bg-amber hover:text-app transition-colors" title="Publish">
                      <Rocket className="w-3.5 h-3.5" />
                    </button>
                    {it.kind === "design" && (
                      <button onClick={() => shareDesign(it)} data-testid={`library-share-${it.id}`}
                        className="p-2 border border-cyan/30 text-cyan hover:bg-cyan hover:text-app transition-colors" title="Share link">
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {it.kind === "design" && (
                      <button onClick={() => del(it.id)} data-testid={`library-delete-${it.id}`} className="p-2 border border-white/10 text-slate-500 hover:text-amber hover:border-amber/40">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ShareDialog open={share.open} onClose={() => setShare({ open: false, url: null })}
        shareUrl={share.url} caption="Check out my custom AETHER design" />

      {/* Publishing pipeline modal */}
      {pub && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-app/80 backdrop-blur-sm p-5" data-testid="publish-modal" onClick={() => setPub(null)}>
          <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto glass-strong clip-chamfer brackets p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPub(null)} className="absolute top-3 right-3 text-slate-400 hover:text-cyan"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-amber/80 mb-1"><Rocket className="w-3.5 h-3.5" /> PUBLISHING PIPELINE</div>
            <h3 className="font-head text-xl font-bold uppercase mb-1 text-slate-100 truncate">{pub.name}</h3>
            <p className="font-mono text-[9px] text-slate-500 mb-4">Library → AI validation → background removal → upscale/QC → mockup → front/back → marketplace optimize → queue</p>

            {!result ? (
              <>
                <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-2">PUBLISH TO</div>
                <div className="space-y-2" data-testid="publish-targets">
                  {targets.map((t) => (
                    <button key={t.id} onClick={() => t.available && toggleTarget(t.id)} disabled={!t.available}
                      data-testid={`publish-target-${t.id}`}
                      className={`w-full flex items-center justify-between p-3 border transition-all ${selected.includes(t.id) ? "border-cyan box-glow-cyan" : "border-white/10"} ${!t.available ? "opacity-50 cursor-not-allowed" : "hover:border-cyan/40"}`}>
                      <span className="font-head text-xs uppercase text-slate-200">{TARGET_LABELS[t.id] || t.id}</span>
                      {t.available
                        ? <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${selected.includes(t.id) ? "bg-cyan border-cyan" : "border-slate-500"}`}>{selected.includes(t.id) && <Check className="w-3 h-3 text-app" />}</span>
                        : <span className="flex items-center gap-1 font-mono text-[8px] tracking-widest text-slate-500"><Lock className="w-2.5 h-2.5" /> KEYS</span>}
                    </button>
                  ))}
                </div>
                <button onClick={doPublish} disabled={busy} data-testid="publish-run-btn"
                  className="btn-hud w-full mt-5 flex items-center justify-center gap-2 border border-amber text-amber py-3 text-sm hover:bg-amber hover:text-app disabled:opacity-40">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Run Pipeline & Publish
                </button>
              </>
            ) : (
              <div data-testid="publish-result">
                <div className="space-y-1.5 mb-4">
                  {result.stages.map((s) => (
                    <div key={s.stage} className="flex items-center justify-between font-mono text-[10px] py-1 border-b border-white/5">
                      <span className="text-slate-400 tracking-wide">{s.stage.replace(/_/g, " ").toUpperCase()}</span>
                      <span className={s.status === "failed" ? "text-amber" : s.mock ? "text-slate-500" : "text-cyan"}>
                        {s.status.toUpperCase()}{s.mock ? " (scaffold)" : ""}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-2">ROUTES</div>
                <div className="space-y-1.5">
                  {result.routes.map((r) => (
                    <div key={r.target} className="flex items-center justify-between font-mono text-[10px]">
                      <span className="text-slate-300">{TARGET_LABELS[r.target] || r.target}</span>
                      <span className={r.status === "published" ? "text-cyan" : r.status === "queued" ? "text-amber" : "text-slate-500"}>{r.status.replace(/_/g, " ").toUpperCase()}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setPub(null)} className="btn-hud w-full mt-5 border border-cyan text-cyan py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
